// src/plugins/posthog.js
import posthog from 'posthog-js'

let initialized = false
const APP_NAME = 'cardea-frontend'
const APP_ENV = process.env.NODE_ENV || 'development'
const API_HOST =
  process.env.VUE_APP_POSTHOG_API_HOST || 'https://us.i.posthog.com'
const PROJECT_TOKEN = process.env.VUE_APP_POSTHOG_TOKEN
const SENSITIVE_KEY_REGEX =
  /(password|senha|authorization|cookie|secret|cpf|bearer|access_token|refresh_token|id_token)/i

function getBuildMetadata() {
  // Values are injected via Vite `define` in vite.config.js.
  // eslint-disable-next-line no-undef
  const version = typeof VERSION !== 'undefined' ? VERSION : null
  // eslint-disable-next-line no-undef
  const commitHash = typeof COMMITHASH !== 'undefined' ? COMMITHASH : null
  // eslint-disable-next-line no-undef
  const branch = typeof BRANCH !== 'undefined' ? BRANCH : null

  return {
    frontend_version: version || process.env.VUE_APP_VERSION || null,
    commit_hash: commitHash || process.env.VUE_APP_COMMIT_HASH || null,
    git_branch: branch || process.env.VUE_APP_GIT_BRANCH || null,
  }
}

function isObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
}

function isReady() {
  return typeof window !== 'undefined' && initialized
}

function redactValue(value, key = '') {
  if (SENSITIVE_KEY_REGEX.test(key)) {
    return '[REDACTED]'
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item))
  }

  if (isObject(value)) {
    return Object.keys(value).reduce((acc, currentKey) => {
      acc[currentKey] = redactValue(value[currentKey], currentKey)
      return acc
    }, {})
  }

  return value
}

export function registerSuperProperties(properties = {}) {
  if (!isReady() || !properties || !Object.keys(properties).length) {
    return
  }
  posthog.register(properties)
}

export function capture(eventName, properties = {}) {
  if (!isReady() || !eventName) {
    return
  }
  posthog.capture(eventName, properties)
}

export function unregisterSuperProperty(propertyName) {
  if (!isReady() || !propertyName) {
    return
  }
  posthog.unregister(propertyName)
}

export function identify(
  distinctId,
  setProperties = {},
  setOnceProperties = {}
) {
  if (!isReady() || !distinctId) {
    return
  }
  posthog.identify(distinctId, setProperties, setOnceProperties)
}

export function reset(resetDeviceId = true) {
  if (!isReady()) {
    return
  }
  posthog.reset(resetDeviceId)
}

export function captureException(error, additionalProperties = {}) {
  if (!isReady() || !error) {
    return
  }
  posthog.captureException(error, additionalProperties)
}

export function isFeatureEnabled(flagKey, defaultValue = false) {
  if (!isReady() || !flagKey) {
    return defaultValue
  }

  try {
    return !!posthog.isFeatureEnabled(flagKey)
  } catch (error) {
    captureException(error, {
      source: 'posthog.isFeatureEnabled',
      flag_key: flagKey,
    })
    return defaultValue
  }
}

export function getFeatureFlag(flagKey, defaultValue = null) {
  if (!isReady() || !flagKey) {
    return defaultValue
  }

  try {
    const value = posthog.getFeatureFlag(flagKey)
    return value == null ? defaultValue : value
  } catch (error) {
    captureException(error, {
      source: 'posthog.getFeatureFlag',
      flag_key: flagKey,
    })
    return defaultValue
  }
}

export function getFeatureFlagPayload(flagKey, defaultValue = null) {
  if (!isReady() || !flagKey) {
    return defaultValue
  }

  try {
    const payload = posthog.getFeatureFlagPayload(flagKey)
    return payload == null ? defaultValue : payload
  } catch (error) {
    captureException(error, {
      source: 'posthog.getFeatureFlagPayload',
      flag_key: flagKey,
    })
    return defaultValue
  }
}

export function onFeatureFlags(callback) {
  if (!isReady() || typeof callback !== 'function') {
    return
  }

  posthog.onFeatureFlags((flags, flagVariants, context) => {
    callback(flags, flagVariants, context || {})
  })
}

export function reloadFeatureFlags() {
  if (!isReady()) {
    return
  }
  posthog.reloadFeatureFlags()
}

export default {
  install(appOrVue) {
    if (!PROJECT_TOKEN) {
      console.warn(
        '[PostHog] VUE_APP_POSTHOG_TOKEN is not defined. PostHog is disabled.'
      )
      return
    }

    posthog.init(PROJECT_TOKEN, {
      api_host: API_HOST,
      person_profiles: 'identified_only',
      feature_flag_request_timeout_ms: 3000,
      before_send: (event) => {
        if (!event) {
          return null
        }

        event.properties = redactValue(event.properties || {})
        event.$set = redactValue(event.$set || {})
        event.$set_once = redactValue(event.$set_once || {})
        return event
      },
    })
    initialized = true
    const buildMetadata = getBuildMetadata()
    posthog.register({
      app_name: APP_NAME,
      app_env: APP_ENV,
      frontend_version: buildMetadata.frontend_version,
      commit_hash: buildMetadata.commit_hash,
      git_branch: buildMetadata.git_branch,
    })

    if (appOrVue?.config?.globalProperties) {
      appOrVue.config.globalProperties.$posthog = posthog
      return
    }

    if (appOrVue?.prototype) {
      appOrVue.prototype.$posthog = posthog
    }
  },
}
